import { ActionGroup, Modal } from '@porsche-code/kado-ui';
import { PButton } from '@porsche-design-system/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NAMESPACES } from '@/config/i18n';
import {
  PersonActionKind,
  useAbilityContext,
  usePersonContext,
} from '@/context';
import {
  MeQuery,
  useDashboardEmailNotificationsQuery,
  useMeQuery,
  useUpdatePersonMutation,
} from '@/graphql';

export const EmailNotificationsHint = () => {
  const { state: ability } = useAbilityContext();
  const { state: person, dispatch: dispatchPerson } = usePersonContext();
  const queryClient = useQueryClient();
  const [button, setButton] = useState<'manage' | 'close'>('manage');
  const [isOpen, setIsOpen] = useState(true);
  const { t } = useTranslation(NAMESPACES);
  const navigate = useNavigate();

  const canSubscribe = ability.can('create', 'DashboardEmailNotification');

  const { isPending: isLoadingNotifications, data: notifications } =
    useDashboardEmailNotificationsQuery(undefined, {
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,

      enabled: canSubscribe,
      select: (data) => data.dashboardEmailNotifications,
    });

  const {
    mutateAsync: updatePerson,
    isPending: isMutating,
    submittedAt,
  } = useUpdatePersonMutation({
    onSuccess: (data) => {
      const cacheKey = useMeQuery.getKey({ isAuthenticated: true } as never);
      const cache = queryClient.getQueryData<MeQuery>(cacheKey);

      if (cache?.me && cache.me.__typename === 'Person') {
        queryClient.setQueryData<MeQuery>(cacheKey, {
          me: {
            ...cache.me,
            acknowledgements: {
              ...cache.me?.acknowledgements,
              emailNotificationsHint:
                data.updatePerson.acknowledgements?.emailNotificationsHint,
            },
          },
        });
      }
    },
  });

  const handleConfirm = async () => {
    await updatePerson(
      {
        input: {
          acknowledgements: {
            emailNotificationsHint: {
              DASHBOARD: new Date().toISOString(),
            },
          },
        },
      },
      {
        onSuccess: (data) =>
          dispatchPerson({
            type: PersonActionKind.SetAcknowledgements,
            payload: {
              emailNotificationsHint:
                data.updatePerson.acknowledgements?.emailNotificationsHint ??
                {},
            },
          }),
      },
    );

    setIsOpen(false);
  };

  // Check if user is allowed to activate notifications
  if (!canSubscribe || isLoadingNotifications) {
    return null;
  }

  // Check if at least one active notification already exists
  if ((notifications?.length ?? 0) > 0) {
    return null;
  }

  // Check if hint was already shown within the last 6 months
  const appConsentDate =
    person.acknowledgements?.emailNotificationsHint?.DASHBOARD;
  if (
    !submittedAt &&
    appConsentDate &&
    dayjs().diff(dayjs(appConsentDate), 'month') < 6
  ) {
    return null;
  }

  return (
    <Modal
      open={isOpen}
      onDismiss={async () => {
        setButton('close');
        await handleConfirm();
      }}
    >
      <Modal.Header>{t('common:emailNotificationsHint.headline')}</Modal.Header>

      {t('common:emailNotificationsHint.description')}

      <ActionGroup>
        <PButton
          type="button"
          variant="secondary"
          loading={isMutating && button === 'close'}
          onClick={async () => {
            setButton('close');
            await handleConfirm();
          }}
          icon="close"
        >
          {t('common:action.close')}
        </PButton>
        <PButton
          type="button"
          variant="primary"
          loading={isMutating && button === 'manage'}
          onClick={async () => {
            await handleConfirm();

            navigate({
              to: '/profile/email-notifications',
            });
          }}
          icon="arrow-head-right"
        >
          {t('common:emailNotificationsHint.manage')}
        </PButton>
      </ActionGroup>
    </Modal>
  );
};
